import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axiosInstance from "../../services/axiosComman";
import { DoughWithData, DoughWithNoData } from "../../utils/DoughNutChart";

/**
 * DashboardChartViewLarge
 * Larger chart card with legends displayed on the RIGHT side (not bottom).
 *
 * Props:
 *  - title       : card heading
 *  - url         : API endpoint (optional – skipped when empty / API not ready)
 *  - path        : navigation target on segment click
 *  - legendItems : (optional) static legend array to override API-driven ones.
 *                  Each item: { label, color, value, filter }
 *                  When provided the chart renders the supplied values directly
 *                  and no API call is made (useful before the backend is ready).
 */
function DashboardChartViewLarge({
  title = "",
  url = "",
  path = "",
  legendItems: staticLegendItems = null,
  filterKey = "statusFilter",
}) {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  // ── API fetch (skipped when staticLegendItems are provided or url is empty) ──
  async function getFunction() {
    await axiosInstance
      .get(url)
      .then((res) => {
        setData(res?.data?.data);
      })
      .catch((err) => {
        console.error(err);
      });
  }

  useEffect(() => {
    if (url) getFunction();
  }, [url]);

  // ── Legend resolution ──────────────────────────────────────────────────────
  // If static legends are given, use them; otherwise derive from API data.
  const resolvedLegendItems = staticLegendItems
    ? staticLegendItems.map((item) => ({
        ...item,
        value: data
          ? (data[item.apiKey || item.filter]?.percentage ?? data[item.filter] ?? item.value)
          : item.value,
        count: data ? (data[item.apiKey || item.filter]?.count ?? data[item.filter]?.count ?? 0) : 0,
      }))
    : [
        {
          label: "Yet To Start",
          color: "#FF0000",
          value: data?.Not_yet_start_percentage || 0,
          filter: "yet to start",
          count: 0,
        },
        {
          label: "In Progress",
          color: "#ffd700",
          value: data?.in_progress_percentage || 0,
          filter: "in progress",
          count: 0,
        },
        {
          label: "Completed",
          color: "#008000",
          value: data?.complete_percentage || 0,
          filter: "completed",
          count: 0,
        },
      ];

  // ── Chart data builder ─────────────────────────────────────────────────────
  function buildChartData() {
    if (resolvedLegendItems && (staticLegendItems || url)) {
      // Use resolved values from legends to build chart data
      const values = resolvedLegendItems.map((i) => i.value);
      const colors = resolvedLegendItems.map((i) => i.color);
      const sum = values.reduce((a, b) => a + b, 0);
      if (sum === 0 && !data) return null; // show No Data state if sum is 0 and no API response

      return {
        data: { label: resolvedLegendItems.map((i) => i.label), data: values },
        bgColor: colors,
      };
    }

    // Fallback for missing data/config
    return null;
  }

  function handleSegmentClick(event, chartElements) {
    const color = chartElements[0]?.element?.options?.borderColor;
    if (staticLegendItems) {
      // Map segment index back to legend filter
      const idx = chartElements[0]?.index;
      if (idx !== undefined && staticLegendItems[idx]?.filter) {
        navigate(path, {
          state: { [filterKey]: staticLegendItems[idx].filter },
        });
      }
      return;
    }
    if (color === "#007300")
      navigate(path, { state: { [filterKey]: "completed" } });
    else if (color === "#E60000")
      navigate(path, { state: { [filterKey]: "yet to start" } });
    else if (color === "#E6C300")
      navigate(path, { state: { [filterKey]: "in progress" } });
  }

  const chartPayload = buildChartData();

  return (
    <div className="w-full h-full overflow-hidden rounded-md p-4 bg-white shadow-main flex flex-col">
      {/* Title */}
      <p className="text-xs 2xl:text-sm text-center text-[#6E6985] mb-2 font-medium">
        {title}
      </p>

      {/* Chart + Side Legend */}
      <div className="flex flex-1 items-center overflow-hidden gap-x-3">
        {/* Doughnut chart */}
        <div
          className="flex-shrink-0 basis-1/2 flex justify-center items-center relative"
          style={{ height: "160px" }}
        >
          {chartPayload ? (
            <>
              <div className="w-[160px] h-[160px] cursor-pointer z-10 mx-auto">
                <DoughWithData
                  bgColor={chartPayload.bgColor}
                  dataWithLabel={chartPayload.data}
                  handleSegmentClick={handleSegmentClick}
                />
              </div>
              {/* Total Count in Center */}
              <div
                className="absolute inset-0 flex flex-col justify-center items-center pointer-events-none"
                style={{ width: "160px", margin: "0 auto" }}
              >
                <span className="text-base 2xl:text-lg font-bold text-[#4F4768]">
                  {data?.total_count || 0}
                </span>
                <span className="text-[10px] 2xl:text-[11px] text-[#6E6985] font-medium uppercase tracking-wider">
                  Total
                </span>
              </div>
            </>
          ) : (
            <DoughWithNoData />
          )}
        </div>

        {/* Side Legends */}
        <div className="flex flex-col basis-1/2 justify-center gap-y-2 flex-1 min-w-0 pr-4">
          {resolvedLegendItems.map((item) => (
            <Link
              key={item.filter ?? item.label}
              to={path}
              state={{ [filterKey]: item.filter }}
              className="flex items-center justify-between gap-x-2 group"
            >
              <span className="flex items-center gap-x-1.5 flex-1 min-w-0">
                <span
                  className="w-2.5 h-2.5 2xl:w-3 2xl:h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: item.color }}
                />
                <span
                  className="text-[11px] 2xl:text-xs font-medium truncate group-hover:underline"
                  style={{ color: item.color }}
                >
                  {item.label}
                </span>
              </span>
              <span className="text-[11px] 2xl:text-xs font-semibold text-[#4F4768] flex-shrink-0">
                {item.count || 0}
              </span>
            </Link>
          ))}

          {/* Extra "Reviewed & Approved" row (API-driven only) */}
          {!staticLegendItems && data?.verified_and_approved ? (
            <div className="flex items-center gap-x-1.5">
              <span className="w-2.5 h-2.5 2xl:w-3 2xl:h-3 rounded-full flex-shrink-0 bg-[#7030A0]" />
              <span className="text-[11px] 2xl:text-xs font-medium text-[#7030A0] truncate">
                Reviewed &amp; Approved
              </span>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default DashboardChartViewLarge;
